/**
 * Admin Menu Configuration
 * 관리자 메뉴 설정
 */

import { AdminUser, ServiceType } from '../models';

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  badge?: string;
  description?: string;
  children?: MenuItem[];
  roles?: string[];
}

export const SERVICE_CONFIG = {
  studyroom: { icon: '🏢', label: '독서실', color: 'blue' },
  academy: { icon: '🎓', label: '학원', color: 'purple' },
  cafe: { icon: '☕', label: '카페', color: 'brown' },
  gym: { icon: '💪', label: '헬스장', color: 'red' },
};

export const ADMIN_MENU_CONFIG: MenuItem[] = [
  {
    id: 'dashboard',
    label: '대시보드',
    icon: '📊',
    path: '/admin/dashboard',
    description: '핵심 지표 한눈에 보기',
  },

  {
    id: 'businesses',
    label: '업체 관리',
    icon: '🏢',
    path: '/admin/businesses',
    badge: 'pendingBusinessCount',
    description: '사업자, 구독, 플랜 통합 관리',
  },

  {
    id: 'sales',
    label: '영업 관리',
    icon: '💼',
    path: '/admin/sales',
    badge: 'newInquiryCount',
    description: '홈페이지 문의부터 계약까지',
  },

  {
    id: 'contracts',
    label: '전자계약',
    icon: '📝',
    path: '/admin/contracts',
    description: '전자계약서 관리 및 발송',
  },

  {
    id: 'servers',
    label: '서버 관리',
    icon: '🖥️',
    path: '/admin/servers',
    description: '고객 배포 서버 모니터링',
    roles: ['super_admin'],
  },

  {
    id: 'migrations',
    label: '서버 이전 이력',
    icon: '📦',
    path: '/admin/migrations',
    description: '서버 이전/마이그레이션 기록',
    roles: ['super_admin'],
  },

  {
    id: 'devices',
    label: '기기 관리',
    icon: '📱',
    path: '/admin/devices',
    description: '태블릿 기기 등록 및 라이선스 관리',
    roles: ['super_admin', 'admin'],
  },

  {
    id: 'billing',
    label: '빌링 관리',
    icon: '💰',
    description: '크레딧 관리',
    children: [
      {
        id: 'credit-requests',
        label: '충전 요청',
        icon: '📥',
        path: '/admin/billing/credit-requests',
        description: '크레딧 충전 요청 승인/거절',
      },
      // 빌링 플랜은 플랜 관리로 통합됨 (2026-01-13)
      // {
      //   id: 'billing-plans',
      //   label: '빌링 플랜',
      //   icon: '📊',
      //   path: '/admin/billing/plans',
      //   description: '빌링 플랜 설정',
      // },
    ],
  },

  {
    id: 'tax',
    label: '세무 관리',
    icon: '🧾',
    description: 'NTD 세무/경비 관리',
    roles: ['super_admin', 'admin'],
    children: [
      {
        id: 'tax-expenses',
        label: '매입/경비',
        icon: '💳',
        path: '/admin/tax/expenses',
        description: '카드 사용내역, 경비 관리',
      },
      {
        id: 'tax-revenues',
        label: '매출',
        icon: '📈',
        path: '/admin/tax/revenues',
        description: '매출 기록 관리',
      },
      {
        id: 'tax-invoices',
        label: '세금계산서',
        icon: '📄',
        path: '/admin/tax/invoices',
        description: '매출/매입 세금계산서 관리',
      },
      {
        id: 'tax-dashboard',
        label: '세무 대시보드',
        icon: '📊',
        path: '/admin/tax/dashboard',
        description: '연간 매출/매입 현황',
      },
      {
        id: 'tax-vat',
        label: '부가세 정리',
        icon: '🧮',
        path: '/admin/tax/vat',
        description: '분기별 부가세 요약',
      },
      {
        id: 'tax-categories',
        label: '경비 분류 설정',
        icon: '🏷️',
        path: '/admin/tax/categories',
        description: '경비 자동분류 키워드 관리',
      },
    ],
  },

  {
    id: 'settings',
    label: '설정',
    icon: '⚙️',
    path: '/admin/settings/profile',
    description: '프로필 및 시스템 설정',
  },
];

// Helper function to filter menu by role
export const filterMenuByRole = (menu: MenuItem[], adminUser: AdminUser): MenuItem[] => {
  return menu
    .map((item) => {
      // Check role permission
      // admin role은 super_admin과 동일한 권한
      const effectiveRole = adminUser.role === 'admin' ? 'super_admin' : adminUser.role;
      if (item.roles && !item.roles.includes(effectiveRole) && !item.roles.includes(adminUser.role)) {
        return null;
      }

      // Recursively filter children
      if (item.children) {
        const filteredChildren = filterMenuByRole(item.children, adminUser);
        if (filteredChildren.length === 0) {
          return null;
        }
        return {
          ...item,
          children: filteredChildren,
        };
      }

      return item;
    })
    .filter((item): item is MenuItem => item !== null);
};
